import axios from "axios";
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import type { PageDescriptor, PageType } from "@pm2/api";
import type { PM2SitemapState, PM2SitemapStore } from "@pm2/sitemap-utils";
import {
  findPageDescriptorByPath,
  findRootPageDescriptor,
  pm2SitemapStore,
} from "@pm2/sitemap-utils";
import type { PM2ConfigStore, PM2ConfigState, PortalConfig } from "@pm2/store";
import { pm2ConfigStore } from "@pm2/store";
import { useConsentSelector } from "@provider/consent";
import { useNavigationManager } from "@provider/navigation";
import type {
  UserAuthenticationProviders,
  UserIdentity,
} from "@provider/user-identity";
import { useUserIdentityState } from "@provider/user-identity";
import { configRefs } from "@utils/constants";

let SESSION_ID: string | undefined;

/**
 * Initializes Gyldendal Clickstream tracking, automatically submitting a
 * tracking event when the user changes page
 */
export function useGyldendalClickstream(): void {
  const navigation = useNavigationManager();
  const user = useUserIdentityState((state) => state.user);
  const hasConsented = useConsentSelector((state) => state.cookies.statistics);

  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();

  useEffect(() => {
    if (!hasConsented) {
      return;
    }

    trackPageView(
      navigation.getLocation().asPath,
      configStore,
      sitemapStore,
      user,
    );

    return navigation.subscribe(() =>
      trackPageView(
        navigation.getLocation().asPath,
        configStore,
        sitemapStore,
        user,
      ),
    );
  }, [navigation, user, hasConsented, configStore, sitemapStore]);
}

async function trackPageView(
  path: string,
  configStore: PM2ConfigStore,
  sitemapStore: PM2SitemapStore,
  user: UserIdentity | undefined | null,
): Promise<void> {
  if (!user) {
    // don't track guest users
    return;
  }

  const configState = configStore.getState().config;
  const sitemapState = sitemapStore.getState();

  if (!configState.trackingTitle) {
    // only track is a title was provided
    return;
  }

  const pageDescriptor = findPageDescriptorByPath(sitemapState, path);

  if (pageDescriptor === undefined) {
    // we couldn't find the page... bail out (don't track 404s)
    return;
  }

  axios.post(
    `${configRefs.apis.clickstream.dangerouslyAssertSyncValue()}/api/Events`,
    {
      type: "View",
      actor: {
        id: user.id,
        type: user.role === "teacher" ? "Laerer" : "Elev",
        loginType: stringifyLoginType(user.authenticationProvider),
        institutionId: user.institution?.id,
        institutionName: user.institution?.name,
        class: user.institution?.class,
        gradeLevel: user.institution?.gradeLevel,
      },
      object: {
        type: stringifyPageType(pageDescriptor.type),
        name: pageDescriptor.title
          ? `${pageDescriptor.title} | ${configState.trackingTitle}`
          : configState.trackingTitle,
        url: resolveAbsoluteUrl(pageDescriptor.path),
        id: pageDescriptor.id,
        targetAudience: stringifyTargetAudience(
          configState.theme.targetAudience,
        ),
        itemPath: pageDescriptor.path,
      },
      context: {
        userAgent: navigator.userAgent,
        sessionId: getSessionId(),
        productId: configState.id,
        productIsbn: configState.uniqueId,
        productType: "Fagportal",
        productVersion: "portalmotor2-simple-skin",
        productTitle: configState.trackingTitle,
        productSection: stringifySectionTitle(
          configState,
          sitemapState,
          pageDescriptor,
        ),
      },
    },
  );
}

function stringifyLoginType(
  loginType: UserAuthenticationProviders | string,
): string | undefined {
  switch (loginType) {
    case "ekey":
      return "Ekey";

    case "uniLogin":
      return "UniLogin";
  }
}

function stringifyPageType(pageType: PageType): string | undefined {
  switch (pageType) {
    case "Page":
      return "Page";

    case "Course":
      return "Course";

    case "Chapter":
      return "Chapter";

    case "ChapterGroup":
      return "ChapterGroup";

    case "Library":
      return "Library";

    case "Shelf":
      return "Bookshelf";

    default:
      // unknown types default to Page
      return "Page";
  }
}

function stringifyTargetAudience(
  targetAudience: PortalConfig["theme"]["targetAudience"],
): string | undefined {
  switch (targetAudience) {
    case "Grade1To3":
      return "Indskoling";

    case "Grade4To6":
      return "Mellemtrin";

    case "Grade7To9":
      return "Udskoling";

    case "Website":
      return "@todo Website";
  }
}

function stringifySectionTitle(
  configState: PM2ConfigState["config"],
  sitemapState: PM2SitemapState,
  pageDescriptor: PageDescriptor,
): string {
  const rootPageDescriptor = findRootPageDescriptor(
    sitemapState,
    pageDescriptor,
  );

  if (rootPageDescriptor.title) {
    return rootPageDescriptor.title;
  }

  // specifically for the front page, we return a hardcoded "Forside" label if
  // nothing else has been provided
  if (
    rootPageDescriptor.path === "/" ||
    rootPageDescriptor.path === configState.homePath
  ) {
    return "Forside";
  }

  // ... otherwise extract the first folder from the path, and capitalize the
  // first letter
  const firstFolderInPath = rootPageDescriptor.path
    .replace(/^\/+/, "")
    .split("/")
    .shift();

  return capitalizeFirstLetter(firstFolderInPath ?? "");
}

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function resolveAbsoluteUrl(pathname: string): string {
  return `${location.protocol}//${location.host}${pathname}`;
}

/**
 * extract id from session storage (if available), otherwise generate a new one
 * and store it for future reference.
 */
function getSessionId(): string {
  // if session id has already been read, then return from in-memory copy to
  // ensure consistency within the current browser session
  if (SESSION_ID === undefined) {
    try {
      // ... otherwise attempt to read it from sessionStorage or generate a new
      // one right
      SESSION_ID = sessionStorage.getItem("sessionId") ?? uuidv4();

      // ... which we store for future reference
      sessionStorage.setItem("sessionId", SESSION_ID);
    } catch {
      // if an error occurred while trying to work with the user's session id,
      // then simply create a new one (this will change on page reloads, but
      // at least it's going to be consistent until that time)
      SESSION_ID = uuidv4();
    }
  }

  return SESSION_ID;
}
